import type {
  ExchangeCodeRequest,
  ExchangeCodeResponse,
  SupersetIntegrationDto,
} from "@superblocksteam/shared";

import { HttpMethod, callServer } from "../../store/utils/client";

/**
 * Fetches the integration metadata that the OAuth callback page needs to drive
 * the rest of the OAuth flow.
 *
 * Why this is not a plain `callServer` call:
 *
 * The OAuth callback page is hosted on `app.superblocks.com` (or a deployed-app
 * host such as `<uuid>.edit.deployed-apps.superblocks.com` in some flows).
 * Shell-driven embed popups authenticate with an explicit Superblocks runtime
 * token. Older embed paths can still fall back to the host-only `embed-token`
 * cookie on whichever host issued it.
 *
 * The default `callServer` base URL is `SUPERBLOCKS_UI_API_ENDPOINT_BASE_URL`,
 * which in production points at `https://api.superblocks.com/api/`. That is a
 * different host from where the cookie lives, so the browser does not send the
 * cookie, the server treats the request as a `VISITOR`, and RBAC returns a 403
 * (`You do not have permission to perform this action.`).
 *
 * Forcing the base URL to the same-origin `/api/` keeps the explicit bearer
 * request and the cookie fallback on the current host. See APPS-4086 for the
 * full investigation.
 */
export async function fetchOAuthCallbackIntegration(
  integrationId: string,
  profileKey: string | undefined,
  accessToken?: string,
): Promise<SupersetIntegrationDto> {
  return callServer<SupersetIntegrationDto>({
    baseUrlOverride: "/api/",
    headers: buildAuthorizationHeaders(accessToken),
    method: HttpMethod.Get,
    query: profileKey ? { profile: profileKey } : {},
    url: `v1/integrations/${integrationId}`,
  });
}

/**
 * POSTs the OAuth `accessCode` to the server to complete the code-exchange
 * step of the OAuth callback flow.
 *
 * Same same-origin override as `fetchOAuthCallbackIntegration` and for the
 * same reason: shell-driven embed popups authenticate with an explicit runtime
 * token, while older embed paths can still fall back to the host-only
 * `embed-token` cookie.
 *
 * Forcing the base URL to the same-origin `/api/` keeps both paths on the
 * current host. See APPS-4086.
 */
export async function exchangeOAuthCallbackCode(
  requestBody: ExchangeCodeRequest,
  accessToken?: string,
): Promise<ExchangeCodeResponse> {
  return callServer<ExchangeCodeResponse>({
    baseUrlOverride: "/api/",
    body: requestBody,
    headers: buildAuthorizationHeaders(accessToken),
    method: HttpMethod.Post,
    url: "v1/oauth2/exchange-code",
  });
}

function buildAuthorizationHeaders(
  accessToken: string | undefined,
): Record<string, string> | undefined {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;
}
